// External libraries
import React, { useState } from 'react';

// Internal modules
import {
  AccordionBody,
  AccordionBodyWrap,
  AccordionHeader,
  Chevron,
  PpwDescription,
  PpwHeaderText,
  PpwIcon,
  PpwLabel,
  Separator,
} from './StyledComponents';
import { PpwCardIcon } from './PpwCardIcon';
import { WalletRow } from './WalletRow';
import type { CheckoutOption } from './types';

export type MultipleWalletOptionsProps = {
  options: CheckoutOption[];
  ppwWalletsLabel: string;
  ppwWalletsDescription: string;
  onWalletClick: (wallet: CheckoutOption) => void;
  footer?: React.ReactNode;
};

export const MultipleWalletOptions = ({
  options,
  ppwWalletsLabel,
  ppwWalletsDescription,
  onWalletClick,
  footer,
}: MultipleWalletOptionsProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const headerId = 'wallets-list-accordion-header';
  const panelId = 'wallets-list-accordion-panel';

  return (
    <li>
      <AccordionHeader
        id={headerId}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        data-testid="wallets-list-accordion-header"
        onClick={() => setIsOpen(prevIsOpen => !prevIsOpen)}
      >
        <PpwIcon className="ppw-icon">
          <PpwCardIcon />
        </PpwIcon>
        <PpwHeaderText>
          <PpwLabel>{ppwWalletsLabel}</PpwLabel>
          <PpwDescription className="ppw-description">
            {ppwWalletsDescription}
          </PpwDescription>
        </PpwHeaderText>
        <Chevron
          className="chevron"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2 5l6 6 6-6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Chevron>
      </AccordionHeader>
      <AccordionBodyWrap
        id={panelId}
        aria-labelledby={headerId}
        aria-hidden={!isOpen}
        isOpen={isOpen}
        data-testid="wallets-list-accordion-body-wrap"
      >
        <AccordionBody>
          {options.map((wallet, index) => (
            <li key={wallet.id}>
              {index > 0 && (
                <Separator
                  data-testid="wallets-list-separator"
                  aria-hidden="true"
                />
              )}
              <WalletRow
                wallet={wallet}
                onWalletClick={onWalletClick}
                tabIndex={isOpen ? undefined : -1}
              />
            </li>
          ))}
          {footer && <li>{footer}</li>}
        </AccordionBody>
      </AccordionBodyWrap>
    </li>
  );
};
